import { useState } from "react";
import { Mail, MapPin, Phone, Instagram } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../contexts/SettingsContext";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll respond within two business days.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
        <div className="space-y-8">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Get in Touch</p>
          <h1 className="font-serif text-5xl md:text-6xl">Speak with the atelier.</h1>
          <div className="divider-gold" />
          <p className="text-muted leading-relaxed max-w-md">
            For private appointments, press enquiries, or wholesale partnerships, our team responds to every message personally.
          </p>
          <ul className="space-y-5 pt-4">
            {settings.contact_email && (
              <li className="flex gap-4 items-start">
                <Mail size={18} strokeWidth={1.5} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted">Email</p>
                  <a href={`mailto:${settings.contact_email}`} className="text-text hover:text-gold" data-testid="contact-page-email">{settings.contact_email}</a>
                </div>
              </li>
            )}
            {settings.contact_phone && (
              <li className="flex gap-4 items-start">
                <Phone size={18} strokeWidth={1.5} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted">Phone</p>
                  <p className="text-text">{settings.contact_phone}</p>
                </div>
              </li>
            )}
            {settings.contact_address && (
              <li className="flex gap-4 items-start">
                <MapPin size={18} strokeWidth={1.5} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted">Atelier</p>
                  <p className="text-text whitespace-pre-line">{settings.contact_address}</p>
                </div>
              </li>
            )}
            {settings.instagram_url && (
              <li className="flex gap-4 items-start">
                <Instagram size={18} strokeWidth={1.5} className="text-gold mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted">Instagram</p>
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text hover:text-gold"
                    data-testid="contact-page-instagram"
                  >@goshi__official</a>
                </div>
              </li>
            )}
          </ul>
        </div>

        <form onSubmit={submit} className="bg-[#141414] p-10 space-y-6 self-start" data-testid="contact-form">
          <input className="input-line" placeholder="Your name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          <input className="input-line" type="email" placeholder="Email address" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          <textarea
            className="input-line resize-none"
            rows={6}
            placeholder="How may we help?"
            required
            value={form.message}
            onChange={e => setForm({ ...form, message: e.target.value })}
            data-testid="contact-message"
          />
          <button className="btn-gold" disabled={busy} data-testid="contact-submit">
            {busy ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
